/**
 * Utility functions for checking user profile completion
 */

import { Employee } from "../types/leave";

/**
 * Required fields for profile completion
 * These fields must have valid entries for a user's profile to be considered complete
 */
const REQUIRED_PROFILE_FIELDS: (keyof Employee)[] = [
  "firstName",
  "lastName",
  "email",
  "homeAddress",
];

/**
 * Check if a user's personal information is complete
 * @param profile - The employee profile to check
 * @returns true if all required fields have valid entries, false otherwise
 */
export const isProfileComplete = (
  profile: Employee | null | undefined,
): boolean => {
  if (!profile) {
    return false;
  }

  // Check if all required fields have non-empty values
  return REQUIRED_PROFILE_FIELDS.every((field) => {
    const value = profile[field];
    // Check if value exists and is not empty string
    return value !== null && value !== undefined && value !== "";
  });
};

/**
 * Get list of missing required fields
 * @param profile - The employee profile to check
 * @returns Array of field names that are missing or empty
 */
export const getMissingProfileFields = (
  profile: Employee | null | undefined,
): (keyof Employee)[] => {
  if (!profile) {
    return REQUIRED_PROFILE_FIELDS;
  }

  return REQUIRED_PROFILE_FIELDS.filter((field) => {
    const value = profile[field];
    return value === null || value === undefined || value === "";
  });
};
